import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Menu, X, UserRound, LogOut, Settings } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Logo } from "./Logo";
import { NotificationBell } from "./NotificationBell";
import { useApp } from "@/lib/app-context";

const LINKS = [
  ["Home", "/", "home"], ["My Wallet", "/wallet", "my-wallet"], ["Add Product", "/add", "add-product"],
  ["Pricing", "/pricing", "pricing"], ["Account", "/account", "account"],
];

const linkCls = ({ isActive }) =>
  `relative px-3 py-2 text-sm font-medium transition-colors duration-200 ${isActive ? "text-ww-emerald" : "text-ww-navy/80 hover:text-ww-emerald"}`;

function AccountMenu() {
  const { user, signOut } = useAuth();
  const { me } = useApp();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const label = me?.name || user?.displayName || user?.email || user?.phoneNumber || "Account";
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-ww-mint text-sm font-bold text-ww-emerald ring-2 ring-white" aria-label="Account menu" data-testid="user-menu-button">
        {user?.photoURL ? <img src={user.photoURL} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : /^[a-z]/i.test(label) ? label[0].toUpperCase() : <UserRound className="h-4 w-4" />}
      </button>
      {open && (
        <div className="absolute right-0 top-11 w-60 rounded-2xl border border-ww-border bg-white p-2 shadow-xl" data-testid="user-menu">
          <p className="truncate px-3 py-2 text-sm font-semibold text-ww-navy">{label}</p>
          <button onClick={() => { setOpen(false); navigate("/account"); }} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-ww-navy hover:bg-ww-mint" data-testid="user-menu-account"><Settings className="h-4 w-4" />Account</button>
          <button onClick={() => signOut({ redirectUrl: "/" })} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-red-600 hover:bg-red-50" data-testid="user-menu-signout"><LogOut className="h-4 w-4" />Sign out</button>
        </div>
      )}
    </div>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const { isSignedIn } = useAuth();
  const { openSignup } = useApp();
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-50 border-b border-ww-border/70 bg-white/80 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-5 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-1 md:flex" aria-label="Main">
          {LINKS.map(([label, to, id]) => (
            <NavLink key={to} to={to} end={to === "/"} className={linkCls} data-testid={`nav-${id}-link`}>{label}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2.5">
          {isSignedIn ? (<>
            <NotificationBell />
            <AccountMenu />
            <button onClick={() => navigate("/wallet")} className="ww-btn-primary hidden sm:inline-flex" data-testid="header-get-started-button">My Wallet</button>
          </>) : (
            <button onClick={openSignup} className="ww-btn-primary whitespace-nowrap max-sm:px-4" data-testid="header-get-started-button">Get Started</button>
          )}
          <button className="rounded-xl p-2 text-ww-navy md:hidden" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-ww-border bg-white px-5 py-3 md:hidden" data-testid="mobile-menu">
          {LINKS.map(([label, to, id]) => (
            <NavLink key={to} to={to} end={to === "/"} onClick={() => setOpen(false)} className={({ isActive }) => `block rounded-xl px-3 py-3 text-base font-medium ${isActive ? "bg-ww-mint text-ww-emerald" : "text-ww-navy"}`} data-testid={`mobile-nav-${id}-link`}>{label}</NavLink>
          ))}
        </nav>
      )}
    </header>
  );
}
